import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CardContent,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ViewListIcon from '@mui/icons-material/ViewList';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import InventoryIcon from '@mui/icons-material/Inventory';
import Layout from '../../components/layout/Layout';
import companySettingsService from '../../services/companySettingsService';
import { useSnackbar } from 'notistack';

const PAGE_KEY_POS_SALES_KEYBOARD = 'POS_SALES_KEYBOARD';

/** Data table column visibility for POS Sales Keyboard. */
const FIELD_VISIBILITY_OPTIONS = [
  { key: 'mrp', label: 'Show MRP column' },
  { key: 'gst', label: 'Show GST (%) column' },
  { key: 'uom', label: 'Show UOM column' },
  { key: 'discount', label: 'Show Discount column' },
];

/** Focus/tab order fields for POS Sales Keyboard. Order number (1, 2, 3, …) or empty to skip. */
const FOCUS_ORDER_FIELDS = [
  { key: 'itemCode', label: 'Item Code' },
  { key: 'qty', label: 'Qty' },
  { key: 'saleRate', label: 'Sale Rate' },
  { key: 'discount', label: 'Discount (% / ₹)' },
];

const PageFieldSettingsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await companySettingsService.getPageFieldConfig(PAGE_KEY_POS_SALES_KEYBOARD);
      // Ensure sensible defaults in case backend returns empty object (older data)
      const safe = data && typeof data === 'object' ? data : {};
      if (safe.roundOffEnabled === undefined) {
        safe.roundOffEnabled = false;
      }
      if (!safe.roundOffMode) {
        safe.roundOffMode = 'NEAREST';
      }
      setConfig(safe);
    } catch {
      setConfig({});
    } finally {
      setLoading(false);
    }
  };

  const getFieldValue = (fieldKey) => config[fieldKey] !== false;

  const setFieldValue = (fieldKey, value) => {
    setConfig((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const getFocusOrderValue = (fieldKey) => {
    const focusOrder = config?.focusOrder;
    if (!focusOrder || typeof focusOrder !== 'object') return '';
    const v = focusOrder[fieldKey];
    if (v == null || v === '') return '';
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 ? String(n) : '';
  };

  const setFocusOrderValue = (fieldKey, value) => {
    setConfig((prev) => {
      const currentFocus = prev.focusOrder && typeof prev.focusOrder === 'object' ? { ...prev.focusOrder } : {};
      if (value === '' || value == null) {
        delete currentFocus[fieldKey];
      } else {
        const n = parseInt(value, 10);
        if (Number.isInteger(n) && n >= 1) currentFocus[fieldKey] = n;
      }
      return { ...prev, focusOrder: currentFocus };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companySettingsService.updatePageFieldConfig(PAGE_KEY_POS_SALES_KEYBOARD, config);
      enqueueSnackbar('POS settings saved. Changes apply when you open the POS screen.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to save POS settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/dashboard" color="inherit" underline="hover">
            Dashboard
          </Link>
          <Typography color="text.primary">POS Settings</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PointOfSaleIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              POS Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Configure columns and focus order for the Point of Sale (billing) screen.
          </Typography>
        </Box>

        <Paper elevation={2} sx={{ overflow: 'hidden' }}>
          <CardContent sx={{ p: 3 }}>
            {/* Field selection – which columns to show */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ViewListIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight="600">
                  Field selection
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which columns to show in the POS sales table.
              </Typography>
              <Grid container spacing={2}>
                {FIELD_VISIBILITY_OPTIONS.map((field) => (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={getFieldValue(field.key)}
                          onChange={(e) => setFieldValue(field.key, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={field.label}
                      sx={{ m: 0 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Focus order – tab order when pressing Enter */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TouchAppIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight="600">
                  Focus order
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Order in which Enter moves the cursor (1 = first, 2 = second, …). Leave empty to skip. After the last field, Enter adds a new row.
              </Typography>
              <Grid container spacing={2}>
                {FOCUS_ORDER_FIELDS.map((f) => (
                  <Grid item xs={6} sm={3} key={f.key}>
                    <TextField
                      size="small"
                      label={f.label}
                      type="number"
                      value={getFocusOrderValue(f.key)}
                      onChange={(e) => setFocusOrderValue(f.key, e.target.value)}
                      placeholder="Skip"
                      fullWidth
                      inputProps={{ min: 1, max: 10, step: 1 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Round off settings */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  Round off
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Control how the final POS amount is rounded when round off is enabled.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(config.roundOffEnabled)}
                      onChange={(e) => setFieldValue('roundOffEnabled', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Enable round off in POS"
                />
              </Box>

              {config.roundOffEnabled && (
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ fontSize: 13, mb: 1 }}>
                    Round off type
                  </FormLabel>
                  <RadioGroup
                    row
                    value={config.roundOffMode || 'NEAREST'}
                    onChange={(e) => setFieldValue('roundOffMode', e.target.value)}
                  >
                    <FormControlLabel
                      value="DOWN"
                      control={<Radio size="small" />}
                      label="Always round down (11.89 → 11)"
                    />
                    <FormControlLabel
                      value="UP"
                      control={<Radio size="small" />}
                      label="Always round up (11.01 → 12)"
                    />
                    <FormControlLabel
                      value="NEAREST"
                      control={<Radio size="small" />}
                      label="Nearest rupee (.49 ↓, .50 ↑)"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Inventory settings */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <InventoryIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight="600">
                  Inventory settings
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Control how stock checks are handled during billing.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(config.allowNegativeStock)}
                      onChange={(e) => setFieldValue('allowNegativeStock', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Allow negative stock billing"
                />
              </Box>
            </Box>

            <Box sx={{ pt: 1 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save POS settings'}
              </Button>
            </Box>
          </CardContent>
        </Paper>
      </Container>
    </Layout>
  );
};

export default PageFieldSettingsPage;
